// src/screens/booking/RegionalRankingScreen.js
// 80차: 지역별 인기 테마 랭킹 — dev 브랜치 첫 실험 기능(신규 매장 유치 세일즈 자료용).
// UNLOCK 등록 매장 한정(전국 랭킹 아님), 실시간 집계 대신 이미 서버에 있는 지난달
// 확정 집계(stats/monthlyPopular, scheduledMonthlyPopularStats)를 그대로 재사용해
// 가볍게 구현 — HomeScreen.js의 "이달의 인기 테마 Top 5"와 동일한 데이터 소스.
// 클로드 디자인 시안(UNLOCK 디자인.dc.html)의 화면 구성을 그대로 따르되, 헤더는
// 기존 앱 공용 AppHeader로 통일(시안엔 자체 헤더가 그려져 있었음).

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { View, Text, StyleSheet, ScrollView, TouchableOpacity, ActivityIndicator } from 'react-native';
import { Image } from 'expo-image';
import { LinearGradient } from 'expo-linear-gradient';
import { useFocusEffect } from '@react-navigation/native';
import { useAuth } from '../../contexts/AuthContext';
import AppHeader, { LogoMark } from '../../components/common/AppHeader';
import { getAllProducts } from '../../services/productService';
import { getMonthlyPopularStats } from '../../services/reservationService';
import { isConfirmedMinorBirth } from '../../utils/dateUtils';
import { COLORS, RADIUS, FONTS } from '../../constants/theme';
import { getGenreMeta, getSeatingTypeLabel } from '../../constants';
import { DifficultyGaugeDots, ClockIcon, StarIcon, IconLabel } from '../../components/common/Icons';

const GOLD = COLORS.gold, SURFACE = COLORS.surface, SURFACE2 = COLORS.surface2;
const BORDER = COLORS.border, TEXT = COLORS.text, TEXT_MUTED = COLORS.textMuted;

// 시안의 금/은/동 뱃지 그라디언트 — 이 화면 전용 장식색이라 theme.js에 별도 토큰화하지 않음
const RANK_GRADIENTS = {
  1: ['#F6E6B8', '#D8AE52'],
  2: ['#E4E4EC', '#A8A8B8'],
  3: ['#E0B48C', '#A87447'],
};
const RANK_TEXT_COLOR = { 1: '#241a06', 2: '#22222c', 3: '#2a1a0c' };

const RankBadge = ({ rank, size = 26 }) => (
  <LinearGradient
    colors={RANK_GRADIENTS[rank]}
    style={[rb.badge, { width: size, height: size, borderRadius: size * 0.35 }]}
  >
    <Text style={[rb.text, { color: RANK_TEXT_COLOR[rank], fontSize: size * 0.5 }]}>{rank}</Text>
  </LinearGradient>
);
const rb = StyleSheet.create({
  badge: { alignItems: 'center', justifyContent: 'center' },
  text:  { fontFamily: FONTS.display, fontWeight: '700' },
});

const ThemeBadges = ({ p }) => {
  const isShow = p.bookingFormat === 'show';
  const gm = p.genre ? getGenreMeta(p.genre) : null;
  return (
    <View style={tb.row}>
      {isShow ? (
        <View style={tb.showBadge}><Text style={tb.showText}>체험형</Text></View>
      ) : (
        <View style={tb.roomBadge}><Text style={tb.roomText}>방탈출</Text></View>
      )}
      {isShow && (
        <View style={tb.seatBadge}><Text style={tb.seatText}>{getSeatingTypeLabel(p.seatingType)}</Text></View>
      )}
      {p.isAdultOnly && (
        <View style={tb.adultBadge}><Text style={tb.adultText}>19금</Text></View>
      )}
      {gm && (
        <View style={[tb.genreChip, { backgroundColor: gm.color + '26', borderColor: gm.color + '59' }]}>
          <Text style={[tb.genreText, { color: gm.color }]}>{p.genre}</Text>
        </View>
      )}
    </View>
  );
};
const tb = StyleSheet.create({
  row:       { flexDirection: 'row', alignItems: 'center', gap: 5, flexWrap: 'wrap' },
  roomBadge: { backgroundColor: COLORS.teal + '22', borderWidth: 1, borderColor: COLORS.teal + '50', borderRadius: RADIUS.sm, paddingHorizontal: 6, paddingVertical: 2 },
  roomText:  { color: COLORS.teal, fontSize: 9.5, fontWeight: '700' },
  showBadge: { backgroundColor: COLORS.blue + '22', borderWidth: 1, borderColor: COLORS.blue + '50', borderRadius: RADIUS.sm, paddingHorizontal: 6, paddingVertical: 2 },
  showText:  { color: COLORS.blue, fontSize: 9.5, fontWeight: '700' },
  seatBadge: { backgroundColor: COLORS.blue + '15', borderWidth: 1, borderColor: COLORS.blue + '35', borderRadius: RADIUS.sm, paddingHorizontal: 6, paddingVertical: 2 },
  seatText:  { color: COLORS.blue, fontSize: 9.5, fontWeight: '600' },
  adultBadge:{ backgroundColor: COLORS.red + '22', borderWidth: 1, borderColor: COLORS.red + '50', borderRadius: RADIUS.sm, paddingHorizontal: 6, paddingVertical: 2 },
  adultText: { color: COLORS.red, fontSize: 9.5, fontWeight: '700' },
  genreChip: { borderRadius: RADIUS.sm, borderWidth: 1, paddingHorizontal: 6, paddingVertical: 2 },
  genreText: { fontSize: 9.5, fontWeight: '600' },
});

const priceLabel = (p) => {
  if (p.bookingFormat === 'show') {
    return p.pricePerPerson > 0 ? `${Number(p.pricePerPerson).toLocaleString()}원 (1인당)` : null;
  }
  const min = p.pricing?.length > 0 ? Math.min(...p.pricing.map(pr => Number(pr.price) || 0)) : p.price;
  return min != null && min > 0 ? `${Number(min).toLocaleString()}원~` : null;
};

// TOP 3 — 강조 카드
const TopCard = ({ p, rank, count, onPress }) => (
  <TouchableOpacity activeOpacity={0.85} onPress={onPress} style={[top.wrap, rank === 1 && top.wrapGold]}>
    {rank === 1 ? (
      <LinearGradient colors={['rgba(216,174,82,0.13)', 'rgba(20,20,31,0.9)']} start={{ x: 0, y: 0 }} end={{ x: 1, y: 1 }} style={StyleSheet.absoluteFill} />
    ) : null}
    <View style={top.thumbWrap}>
      {p.thumbnailUrl ? <Image source={{ uri: p.thumbnailUrl }} style={top.thumb} />
        : <View style={[top.thumb, top.placeholder]}><LogoMark size={20} /></View>}
      <View style={top.badgeAnchor}><RankBadge rank={rank} /></View>
    </View>
    <View style={top.info}>
      <ThemeBadges p={p} />
      <Text style={top.title} numberOfLines={1}>{p.name}</Text>
      <Text style={top.store} numberOfLines={1}>
        {p.storeName}{p.branchName ? ` · ${p.branchName}` : ''}
      </Text>
      <View style={top.metaRow}>
        <IconLabel Icon={StarIcon} size={11} color={GOLD} textStyle={top.rating}>{Number(p.rating || 0).toFixed(1)}</IconLabel>
        <Text style={top.reviewCount}>({p.reviewCount || 0})</Text>
        {p.bookingFormat !== 'show' && p.difficulty && (
          <DifficultyGaugeDots difficulty={p.difficulty} size={10} gap={3} color={COLORS.goldLight} mutedColor="#333" />
        )}
        {priceLabel(p) && <Text style={top.price}>{priceLabel(p)}</Text>}
      </View>
    </View>
  </TouchableOpacity>
);
const top = StyleSheet.create({
  wrap:        { flexDirection: 'row', gap: 13, backgroundColor: SURFACE, borderWidth: 1, borderColor: 'rgba(255,255,255,0.1)', borderRadius: 16, padding: 13, overflow: 'hidden' },
  wrapGold:    { borderColor: 'rgba(216,174,82,0.45)' },
  thumbWrap:   { width: 78, height: 78, flexShrink: 0 },
  thumb:       { width: '100%', height: '100%', borderRadius: 12, backgroundColor: SURFACE2 },
  placeholder: { alignItems: 'center', justifyContent: 'center' },
  badgeAnchor: { position: 'absolute', top: -6, left: -6 },
  info:        { flex: 1, gap: 5, minWidth: 0, justifyContent: 'center' },
  title:       { color: TEXT, fontSize: 15.5, fontFamily: FONTS.bodyBold },
  store:       { color: TEXT_MUTED, fontSize: 12 },
  metaRow:     { flexDirection: 'row', alignItems: 'center', gap: 6 },
  rating:      { color: GOLD, fontSize: 12, fontWeight: '700' },
  reviewCount: { color: TEXT_MUTED, fontSize: 11, marginRight: 4 },
  price:       { color: '#D6D6DE', fontSize: 12 },
});

// 4위 이하 — 컴팩트 리스트 행
const RankRow = ({ p, rank, onPress }) => (
  <TouchableOpacity activeOpacity={0.85} onPress={onPress} style={row.wrap}>
    <Text style={row.rankNum}>{rank}</Text>
    <View style={row.thumbWrap}>
      {p.thumbnailUrl ? <Image source={{ uri: p.thumbnailUrl }} style={row.thumb} />
        : <View style={[row.thumb, row.placeholder]}><LogoMark size={16} /></View>}
    </View>
    <View style={row.info}>
      <ThemeBadges p={p} />
      <Text style={row.title} numberOfLines={1}>{p.name}</Text>
      <Text style={row.sub} numberOfLines={1}>
        {p.storeName}{p.branchName ? ` · ${p.branchName}` : ''}
        {` · ★${Number(p.rating || 0).toFixed(1)}(${p.reviewCount || 0})`}
        {priceLabel(p) ? ` · ${priceLabel(p)}` : ''}
      </Text>
    </View>
  </TouchableOpacity>
);
const row = StyleSheet.create({
  wrap:        { flexDirection: 'row', alignItems: 'center', gap: 13 },
  rankNum:     { width: 20, textAlign: 'center', fontFamily: FONTS.display, fontSize: 15, fontWeight: '600', color: TEXT_MUTED, flexShrink: 0 },
  thumbWrap:   { width: 56, height: 56, flexShrink: 0 },
  thumb:       { width: '100%', height: '100%', borderRadius: 10, backgroundColor: SURFACE2 },
  placeholder: { alignItems: 'center', justifyContent: 'center' },
  info:        { flex: 1, gap: 4, minWidth: 0 },
  title:       { color: TEXT, fontSize: 14, fontFamily: FONTS.bodyBold },
  sub:         { color: TEXT_MUTED, fontSize: 11.5 },
});

const RegionChip = ({ label, active, onPress }) => (
  <TouchableOpacity onPress={onPress} style={[rc.chip, active && rc.active]}>
    <Text style={[rc.text, active && rc.activeText]}>{label}</Text>
  </TouchableOpacity>
);
const rc = StyleSheet.create({
  chip:       { paddingHorizontal: 15, paddingVertical: 9, borderRadius: RADIUS.pill, borderWidth: 1, borderColor: 'rgba(255,255,255,0.08)', backgroundColor: SURFACE2 },
  active:     { borderColor: GOLD, backgroundColor: GOLD },
  text:       { color: '#C8C8D2', fontSize: 13, fontWeight: '600' },
  activeText: { color: COLORS.onGold },
});

export default function RegionalRankingScreen({ navigation }) {
  const { user, profile } = useAuth();
  const [allProducts, setAllProducts] = useState([]);
  const [popularStats, setPopularStats] = useState({ month: null, productCounts: {} });
  const [loading, setLoading] = useState(true);
  const [selectedRegion, setSelectedRegion] = useState(null); // null = 전체

  const load = useCallback(async () => {
    try {
      const isMinor = isConfirmedMinorBirth(profile?.birth);
      const [all, stats] = await Promise.all([
        getAllProducts({ uid: user?.uid, isMinor }),
        getMonthlyPopularStats().catch(() => ({ month: null, productCounts: {} })),
      ]);
      setAllProducts(all);
      setPopularStats(stats);
    } catch (_) {
      // 조용히 빈 상태로 폴백 — 이 화면은 부가 기능이라 실패해도 앱 전체에 영향 없음
    } finally {
      setLoading(false);
    }
  }, [user?.uid, profile?.birth]);

  useFocusEffect(useCallback(() => { load(); }, [load]));

  const ranked = useMemo(() => {
    const counts = popularStats.productCounts || {};
    return allProducts
      .filter(p => (counts[p.id] || 0) > 0)
      .filter(p => !selectedRegion || p.subRegion === selectedRegion)
      .sort((a, b) => (counts[b.id] || 0) - (counts[a.id] || 0));
  }, [allProducts, popularStats, selectedRegion]);

  // 실제로 랭킹에 오른 테마들이 속한 지역만 칩으로 노출(빈 지역 칩은 안 보여줌)
  const availableRegions = useMemo(() => {
    const counts = popularStats.productCounts || {};
    const set = new Set(
      allProducts.filter(p => (counts[p.id] || 0) > 0 && p.subRegion).map(p => p.subRegion)
    );
    return [...set];
  }, [allProducts, popularStats]);

  const monthLabel = popularStats.month ? `${Number(popularStats.month.split('-')[1])}월` : null;
  const goDetail = (p) => navigation.navigate('ThemeDetail', { product: p });
  const top3 = ranked.slice(0, 3);
  const rest = ranked.slice(3);

  return (
    <View style={s.container}>
      <AppHeader showBack centerElement={<Text style={s.headerTitle}>지역별 인기 테마</Text>} />
      {loading ? (
        <View style={s.loadingWrap}><ActivityIndicator color={GOLD} /></View>
      ) : (
        <ScrollView contentContainerStyle={s.scroll}>
          {availableRegions.length > 0 && (
            <ScrollView horizontal showsHorizontalScrollIndicator={false} style={s.chipScroll} contentContainerStyle={s.chipRow}>
              <RegionChip label="전체" active={!selectedRegion} onPress={() => setSelectedRegion(null)} />
              {availableRegions.map(r => (
                <RegionChip key={r} label={r} active={selectedRegion === r} onPress={() => setSelectedRegion(selectedRegion === r ? null : r)} />
              ))}
            </ScrollView>
          )}

          {monthLabel && (
            <View style={s.metaRow}>
              <ClockIcon size={13} color="#7A7A88" />
              <Text style={s.metaText}>{monthLabel} 기준 · UNLOCK 예약 건수 집계</Text>
            </View>
          )}

          {ranked.length === 0 ? (
            <View style={s.emptyWrap}>
              <Text style={s.emptyText}>
                {selectedRegion ? `${selectedRegion} 지역엔 아직 집계된 예약이 없어요` : '아직 집계된 인기 테마가 없어요'}
              </Text>
            </View>
          ) : (
            <>
              <View style={s.topList}>
                {top3.map((p, i) => (
                  <TopCard key={p.id} p={p} rank={i + 1} onPress={() => goDetail(p)} />
                ))}
              </View>
              {rest.length > 0 && (
                <>
                  <View style={s.divider} />
                  <View style={s.restList}>
                    {rest.map((p, i) => (
                      <RankRow key={p.id} p={p} rank={i + 4} onPress={() => goDetail(p)} />
                    ))}
                  </View>
                </>
              )}
            </>
          )}
        </ScrollView>
      )}
    </View>
  );
}

const s = StyleSheet.create({
  container:  { flex: 1, backgroundColor: COLORS.bg },
  headerTitle:{ color: TEXT, fontSize: 17, fontFamily: FONTS.bodyBold },
  loadingWrap:{ flex: 1, alignItems: 'center', justifyContent: 'center' },
  scroll:     { paddingBottom: 40 },
  chipScroll: { flexGrow: 0 },
  chipRow:    { flexDirection: 'row', gap: 8, paddingHorizontal: 16, paddingTop: 12, paddingBottom: 12 },
  metaRow:    { flexDirection: 'row', alignItems: 'center', gap: 6, paddingHorizontal: 16, paddingBottom: 16 },
  metaText:   { fontSize: 11.5, color: '#7A7A88' },
  emptyWrap:  { paddingHorizontal: 16, paddingVertical: 60, alignItems: 'center' },
  emptyText:  { color: TEXT_MUTED, fontSize: 13, textAlign: 'center' },
  topList:    { gap: 10, paddingHorizontal: 16 },
  divider:    { height: 1, backgroundColor: 'rgba(255,255,255,0.07)', marginHorizontal: 16, marginVertical: 14 },
  restList:   { gap: 14, paddingHorizontal: 16 },
});
